import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { useRequestModal } from "../context/RequestModalContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppButton } from "../components/WhatsAppButton";
import { MobileNav } from "../components/MobileNav";
import { Reveal } from "../components/Reveal";
import { ServiceForms } from "../components/ServiceForms";
import { whatsAppSimple } from "../lib/api";
import { DESTINATIONS, destinationIndex, interp } from "../lib/destinations";
import { DESTINATION_ORDERED, IMAGES } from "../lib/images";
import { destinationContent } from "../lib/destinationContent";
import { serviceCopy, servicePillars, cityFaq } from "../lib/serviceContent";

const SITE = "https://velmonte.es";

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", href);
}
function setJsonLd(obj) {
  let el = document.getElementById("dest-jsonld");
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = "dest-jsonld"; document.head.appendChild(el); }
  el.textContent = JSON.stringify(obj);
}
function setFaqJsonLd(faqs) {
  let el = document.getElementById("dest-faq-jsonld");
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = "dest-faq-jsonld"; document.head.appendChild(el); }
  el.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((x) => ({ "@type": "Question", name: x.q, acceptedAnswer: { "@type": "Answer", text: x.a } })),
  });
}

export default function DestinationPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { openRequest } = useRequestModal();

  const idx = destinationIndex(slug);

  useEffect(() => {
    if (idx < 0) { navigate("/", { replace: true }); return; }
    const localizedList = t("destinations.list") || [];
    const name = localizedList[idx] || DESTINATIONS[idx].name;
    document.title = interp(t("destPage.seoTitle"), name);
    setMeta("description", interp(t("destPage.seoDesc"), name));
    setCanonical(`${SITE}/destinations/${slug}`);
    const pillars = servicePillars(slug);
    setJsonLd({
      "@context": "https://schema.org",
      "@type": ["Service", "TravelAgency"],
      name: `VELMONTÉ — ${name}`,
      serviceType: "Luxury concierge & private office",
      url: `${SITE}/destinations/${slug}`,
      areaServed: { "@type": "Place", name },
      description: interp(t("destPage.seoDesc"), name),
      provider: { "@type": "Organization", name: "VELMONTÉ", url: SITE, telephone: "+34645459043" },
      availableLanguage: ["en", "es", "ar", "de", "fr"],
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: `Services in ${name}`,
        itemListElement: pillars.map((p) => ({
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: serviceCopy("en", p.service, name, slug).heading },
        })),
      },
    });
    window.scrollTo(0, 0);
    setFaqJsonLd(cityFaq(lang, name, slug));
    return () => {
      document.title = "VELMONTÉ — Private Office & Lifestyle Management | Anything. Anywhere.";
      const j = document.getElementById("dest-jsonld");
      if (j) j.remove();
      const fj = document.getElementById("dest-faq-jsonld");
      if (fj) fj.remove();
    };
  }, [slug, lang, idx, navigate, t]);

  if (idx < 0) return null;

  const localizedList = t("destinations.list") || [];
  const name = localizedList[idx] || DESTINATIONS[idx].name;
  const heroImg = DESTINATION_ORDERED[idx] || IMAGES.coastline;
  const localCopy = destinationContent[slug] ? destinationContent[slug][lang] : null;
  const faqs = cityFaq(lang, name, slug);

  const pillars = servicePillars(slug);

  return (
    <div className="vm-grain relative">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative min-h-[70vh] md:min-h-[80vh] flex items-end overflow-hidden" data-testid="destination-hero">
          <div className="absolute inset-0">
            <img src={heroImg} alt={`VELMONTÉ — ${name}`} className="w-full h-full object-cover scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-vm-bg via-vm-bg/40 to-black/40" />
          </div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
            <motion.p className="vm-label mb-5" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}>
              {t("destPage.eyebrow")}
            </motion.p>
            <motion.h1 className="font-serif font-light leading-[0.95] text-6xl md:text-8xl" initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }}>
              {name}
            </motion.h1>
            <motion.p className="mt-7 max-w-2xl text-base md:text-lg text-vm-text2 font-light leading-relaxed" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.5 }}>
              {interp(t("destPage.intro"), name)}
            </motion.p>
            <motion.div className="mt-9 flex flex-col sm:flex-row gap-4" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.65 }}>
              <button onClick={() => openRequest("other", `Destination: ${name}. `)} className="vm-btn-primary px-9 py-4 text-xs tracking-[0.2em] uppercase" data-testid="destination-request-button">
                {interp(t("destPage.requestCta"), name)}
              </button>
              <a href={whatsAppSimple(`VELMONTÉ — ${name}: `)} target="_blank" rel="noopener noreferrer" className="vm-btn-ghost px-9 py-4 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2" data-testid="destination-whatsapp-button">
                <MessageCircle strokeWidth={1.5} size={16} /> {t("common.whatsapp")}
              </a>
            </motion.div>
          </div>
        </section>

        {/* Editorial unique copy (SEO) */}
        {localCopy && (
          <section className="py-20 md:py-28 border-b border-vm-border" data-testid="destination-about">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
              <Reveal>
                <p className="vm-label mb-6">{name}</p>
                <p className="font-serif text-2xl md:text-3xl font-light leading-relaxed text-vm-text2">{localCopy}</p>
              </Reveal>
            </div>
          </section>
        )}

        {/* Services */}
        <section className="py-24 md:py-32 border-b border-vm-border">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal>
              <h2 className="font-serif font-light leading-[1] text-4xl md:text-5xl mb-14">{interp(t("destPage.heading"), name)}</h2>
            </Reveal>
            <div className="grid md:grid-cols-2 gap-px bg-vm-border">
              {pillars.map((p, i) => {
                const copy = serviceCopy(lang, p.service, name, slug);
                return (
                <Reveal key={p.service} delay={(i % 2) * 0.08}>
                  <div className="bg-vm-bg group relative overflow-hidden h-full" data-testid={`destination-pillar-${p.service}`}>
                    <div className="overflow-hidden h-56 md:h-72">
                      <img src={p.img} alt={copy.heading} loading="lazy" className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-[1100ms]" />
                    </div>
                    <div className="p-8 md:p-10">
                      <p className="vm-label mb-3">{copy.label || t(`${p.ns}.label`)}</p>
                      <h3 className="font-serif text-2xl md:text-3xl font-light mb-4">{copy.heading}</h3>
                      <p className="text-vm-text2 font-light leading-relaxed text-sm mb-6 max-w-md">{copy.body}</p>
                      <button onClick={() => openRequest(p.cat, `Destination: ${name}. `)} className="vm-btn-ghost px-7 py-3 text-xs tracking-[0.2em] uppercase" data-testid={`destination-pillar-cta-${p.service}`}>
                        {copy.cta || t(`${p.ns}.cta`)}
                      </button>
                    </div>
                  </div>
                </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Service request forms (aviation / yacht / villa) */}
        <section className="py-24 md:py-32 border-b border-vm-border" data-testid="destination-forms-section">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <Reveal>
              <ServiceForms destinationName={name} />
            </Reveal>
          </div>
        </section>

        {/* Concierge callout */}
        <section className="py-24 md:py-36 border-b border-vm-border">
          <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
            <Reveal>
              <p className="vm-label mb-6">{t("concierge.label")}</p>
              <h2 className="font-serif font-light leading-[1.05] text-4xl md:text-6xl mb-8">{t("concierge.title")}</h2>
              <p className="font-serif text-2xl md:text-3xl font-light text-vm-accent mb-10">{t("concierge.closing")}</p>
              <button onClick={() => openRequest("other", `Destination: ${name}. `)} className="vm-btn-primary px-9 py-4 text-xs tracking-[0.2em] uppercase" data-testid="destination-concierge-cta">
                {interp(t("destPage.requestCta"), name)}
              </button>
            </Reveal>
          </div>
        </section>

        {/* FAQ (per city) */}
        <section className="py-24 md:py-32 border-b border-vm-border" data-testid="destination-faq">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <Reveal>
              <p className="vm-label mb-6">FAQ · {name}</p>
              <h2 className="font-serif font-light leading-[1.05] text-3xl md:text-5xl mb-12">{interp(t("destPage.heading"), name)}</h2>
            </Reveal>
            <div className="divide-y divide-vm-border border-t border-vm-border">
              {faqs.map((item, i) => (
                <Reveal key={i} delay={(i % 3) * 0.05}>
                  <details className="group py-6" data-testid={`faq-item-${i}`}>
                    <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                      <span className="font-serif text-xl md:text-2xl font-light text-vm-text">{item.q}</span>
                      <span className="text-vm-accent text-2xl leading-none shrink-0 transition-transform duration-300 group-open:rotate-45">+</span>
                    </summary>
                    <p className="mt-4 text-vm-text2 font-light leading-relaxed text-sm md:text-base max-w-2xl">{item.a}</p>
                  </details>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Other destinations */}
        <section className="py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Link to="/#destinations" className="inline-flex items-center gap-2 vm-label hover:text-vm-text transition-colors mb-10" data-testid="destination-back-link">
              <ArrowLeft strokeWidth={1.5} size={14} /> {t("destPage.back")}
            </Link>
            <div className="flex flex-wrap gap-3">
              {DESTINATIONS.map((d, i) => (
                <Link key={d.slug} to={`/destinations/${d.slug}`} className={`text-xs tracking-[0.15em] uppercase px-4 py-2 border transition-colors duration-300 ${d.slug === slug ? "border-vm-accent text-vm-accent" : "border-vm-border text-vm-text2 hover:border-vm-accent hover:text-vm-text"}`} data-testid={`other-destination-${d.slug}`}>
                  {localizedList[i] || d.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <MobileNav />
    </div>
  );
}
